import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { DataObject } from '../shared/core/domain/data-object';
import { User, UserRoleName } from './auth.model';
import { firstValueFrom, Observable, of } from 'rxjs';
import { handleError } from '../shared/core/infrastructure/error';
import { AppRoutes } from '../app/app.routes';
import { Router } from '@angular/router';

export class UserClass {
  constructor(public user: User) {
    this.initials = (this.user.firstName?.charAt(0)?.toUpperCase() ?? 'U') + "." + (this.user.lastName?.charAt(0)?.toUpperCase() ?? "N");
  }

  public initials = "U.N";

  public userHasPermission(role: UserRoleName): boolean {
    return this.user.roles.some(r => r.name === role);
  }

  public userHasOneOfPermission(roles: UserRoleName[]): boolean {
    return roles.some((roleName: UserRoleName) => this.userHasPermission(roleName));
  }
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private apiUrl: string = `${environment.apiUrl}`;
  public account = signal<UserClass | null>(null);

  me(refresh?: boolean) {
    if (!this.account() || refresh) {
      return this.http.get<User>(`${this.apiUrl}/backoffice/me`, { credentials: 'include' }).pipe(
        map(res => {
          this.account.set(new UserClass(res));
          return new DataObject<User | null>(null, res);
        }),
        catchError(e => of<DataObject<User | null>>(handleError(e))),
      );
    }

    return of(new DataObject<User | null>(null, this.account()!.user));
  }

  public isLoggedIn(): Observable<boolean> {
    return this.me().pipe(
      map(res => !!res.data)
    );
  }

  public redirectToSSOLogin(redirectUrl?: string) {
    const returnUrl = redirectUrl ?? encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`${environment.apiUrl}/public/auth/saml/login?returnUrl=${returnUrl}`);
  }

  public logout() {
    return firstValueFrom(this.http.post(
      `${ this.apiUrl }/public/auth/logout`,
      {},
      { withCredentials: true, credentials: 'include' }
    ).pipe(
      map(() => {
        this.account.set(null);
        setTimeout(() => {
          this.router.navigate(["/", AppRoutes.login]).then(() => {
            window.location.reload();
          });
        });
        return new DataObject<void>(null);
      }),
      catchError(e => of<DataObject<void>>(handleError(e))),
    ));
  }
}
